import { DDSTooltip, DDSTypography } from 'den-design-system'
import { Notification, Settings, UserFollow } from '@carbon/icons-react'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react'
import { UrlWithOrgProjEnvIds, replaceUrl } from '../../../utils/UrlUtils'
import { useNavigate, useSearchParams } from 'react-router-dom'

import HttpConstants from '../../../constants/HttpConstants'
import StringConstants from '../../../constants/StringConstants'
import Tooltip from '../Tooltip/Tooltip'
import { addQueryParamsToUrl } from '../../../utils/UrlGenerator'
import { gaPageConstants } from '../../../constants/GAConstants'
import { getSideBarTabs } from '../../../utils/SidebarUtils'
import { isEmpty } from 'lodash'
import { observer } from 'mobx-react-lite'
import pallete from '../../../global/newPallete'
import routeConstants from '../../../constants/RouteConstants'
import { useAnalyticsEventTracker } from '../../../utils/GAUtils'
import { useStoreContext } from '../../../store/StoreContext'

interface SidebarItem {
  id: number
  title: string
  leftIcon: JSX.Element
  link: string
  topIcon: any
  tourId: any
  sidebarTitle: any
}

interface SidebarProps {
  isSidebarExpanded: boolean
  palette: any
  organizations: any[]
  projectType: any
}

export interface SidebarRef {
  handleSidebarItemClick: (item: SidebarItem) => void
}

const SidebarLayout = forwardRef<SidebarRef, SidebarProps>((props, ref) => {
  const { isSidebarExpanded, palette, organizations, projectType } = props

  const navigate = useNavigate()
  const store = useStoreContext()

  const [searchParams, setSearchParams] = useSearchParams()
  const gaEventTracker = useAnalyticsEventTracker(gaPageConstants.SIDE_NAVBAR)
  const [sidebarItems, setSidebarItems] = useState<SidebarItem[]>([])
  const [selectedOption, setselectedOption] = React.useState<any>(-1)
  const handleButtonClick = (buttonId: any) => {
    setselectedOption(buttonId)
  }

  const NotificationSidebarTabs = [
    {
      title: 'Notifications',
      link: routeConstants.NOTIFICATIONS,
      leftIcon: <Notification />,
      topIcon: <Notification size={24} color={pallete.colors.primary2} />
    },
    {
      title: 'Invitations',
      leftIcon: <UserFollow />,
      link: routeConstants.INVITES,
      topIcon: <UserFollow size={24} color={pallete.colors.primary2} />
    }
  ]

  const SettingSidebarTabs = [
    {
      title: 'Profile',
      link: routeConstants.ACCOUNT_SETTING,
      topIcon: <Settings size={24} color={pallete.colors.primary2} />
    },
    {
      title: 'Organization Settings',
      link: routeConstants.MANAGE_ORGANIZATION,
      topIcon: <Settings size={24} color={pallete.colors.primary2} />
    },
    {
      title: 'Project Settings',
      link: routeConstants.PROJECT_SETTING,
      topIcon: <Settings size={24} color={pallete.colors.primary2} />
    }
  ]

  useImperativeHandle(ref, () => ({
    handleSidebarItemClick
  }))

  useEffect(() => {
    if (!sessionStorage.getItem(StringConstants.ACTIVE_TAB))
      sessionStorage.setItem(
        StringConstants.ACTIVE_TAB,
        StringConstants.TAB_VALUES.OVERVIEW
      )
  }, [])

  useEffect(() => {
    if (!sessionStorage.getItem(StringConstants.ACTIVE_TAB))
      sessionStorage.setItem(
        StringConstants.ACTIVE_TAB,
        StringConstants.TAB_VALUES.OVERVIEW
      )
  }, [])

  useEffect(() => {
    const fetchSidebarItems = async () => {
      const userRole = store.userStore.getUserRole()
      const tabs = await getSideBarTabs(
        userRole,
        store.scopeStore.getSelectedProject().type
      )
      setSidebarItems(tabs)
    }
    fetchSidebarItems()
  }, [store.userStore, projectType, store.scopeStore.getSelectedProject()])

  useEffect(() => {
    const organizationId = store.scopeStore.getScope()['organization-id']
    const organization = organizations.filter(
      (record: any) => record.id === organizationId
    )
    if (organization.length > 0) {
      const selectedOrg = organization[0]
      store.userStore.setSelectedOrganization(selectedOrg)

      const isNoActiveSubscription =
        !selectedOrg.subscription ||
        selectedOrg.subscription.status === 'canceled'

      store.userStore.setNoActiveSubscription(isNoActiveSubscription)
    }

    if (
      !window.location.pathname.includes('preferences') &&
      !window.location.pathname.includes('organization')
    ) {
      store.uiStore.setGlobalLoader(true)
    } else {
      store.uiStore.setGlobalLoader(false)
    }
  }, [store.scopeStore.getOrganizationId()])

  React.useEffect(() => {
    if (
      window.location.pathname !== routeConstants.INVITES &&
      window.location.pathname !== routeConstants.NOTIFICATIONS
    ) {
      SettingTabItemHandler()
      TabItemHandler()
    } else {
      NotificationTabItemHandler()
    }
  }, [
    window.location.pathname,
    store.scopeStore.getSelectedProject(),
    searchParams
  ])

  const TabItemHandler = () => {
    store.uiStore.setNotificationSideNavbar(false)

    getSideBarTabs(
      store.userStore.getUserRole(),
      store.scopeStore.getSelectedProject().type
    ).map((item: any) => {
      const pathname = window.location.pathname.split('/')
      if (item) {
        if (pathname.includes(item.link.split('/')[1])) {
          if (item.link.split('/')[1] === 'settings') {
            SettingTabItemHandler()
          } else {
            setselectedOption(item.id)
            store.uiStore.setSidebarItem(item)
          }
        }
      }
    })
  }

  const NotificationTabItemHandler = () => {
    store.uiStore.setNotificationSideNavbar(true)
    NotificationSidebarTabs.map((item) => {
      const params = new URLSearchParams(window.location.search)
      const innerTab = params.get('innerTab')
      if (
        (item.link === '/allNotifications' && item.title === 'Notifications') ||
        (item.link === '/allInvites' &&
          innerTab === '2' &&
          item.title === 'Invitations')
      ) {
        store.uiStore.setSidebarItem(item)
        setselectedOption(item.link)
      }
    })
  }

  const SettingTabItemHandler = () => {
    store.uiStore.setNotificationSideNavbar(true)

    SettingSidebarTabs.forEach((item) => {
      if (
        (window.location.href.includes('/accountSettings') &&
          item.title === 'Profile') ||
        (window.location.href.includes('/manageOrganization') &&
          item.title === 'Organization Settings') ||
        (window.location.href.includes('/projects') &&
          item.title === 'Project Settings')
      ) {
        store.uiStore.setSidebarItem(item)
        setselectedOption(item.link)
      }
    })
  }

  const handleSidebarItemClick = (item: SidebarItem) => {
    store.filterStore.setFiltersApplied(false)
    store.breadcrumbStore.reset()
    if (store.scopeStore.getProjectId()?.length) {
      handleButtonClick(item.id)

      const organizationId = store.scopeStore.getScope()[
        'organization-id'
      ] as string
      const projectId = store.scopeStore.getScope()['project-id'] as string
      const environmentId = store.scopeStore.getScope()[
        'environment-id'
      ] as string
      if (item.id === selectedOption) {
        const currentUrl = window.location.pathname

        const existingUrl = replaceUrl(
          item.link,
          organizationId,
          projectId,
          environmentId
        )
        if (currentUrl !== existingUrl) {
          navigate(existingUrl)
        } else {
          return
        }
      }
      gaEventTracker(item.title + ' Tab')
      setSearchParams({})

      if (item.title !== 'Settings') {
        if (!isEmpty(store.scopeStore.getScope()['project-id'])) {
          navigate(
            '../' +
              replaceUrl(item.link, organizationId, projectId, environmentId)
          )
        } else {
          navigate('../' + replaceUrl(item.link, organizationId, '', ''))
        }
      } else {
        navigate(
          replaceUrl(item.link, organizationId, projectId, environmentId)
        )
      }
      TabItemHandler()
    } else {
      navigate(
        addQueryParamsToUrl(
          routeConstants.REDIRECT,
          {
            redirect: UrlWithOrgProjEnvIds(item.link, store)
          },

          HttpConstants.GET_METHOD
        )
      )
    }
  }
  const toolTipStyle: React.CSSProperties = {
    borderColor: pallete.colors.stroke2,
    backgroundColor: pallete.colors.surface4,
    minWidth: 'max-content',
    position: 'fixed',
    zIndex: 99999
  }

  return (
    <div
      id='sidebar-item'
      className={`${
        store.uiStore.getGlobalLoader() ||
        store.uiStore.getprojectEnvironmentLoader() ||
        (store.projectStore.getLoading() &&
          'cursor-not-allowed pointer-events-none')
      }`}
    >
      {sidebarItems.map((item, index) => (
        <>
          <div
            key={index}
            className={`pb-[12px] select-none  ${
              store.uiStore.getGlobalLoader() ||
              store.projectStore.getLoading() ||
              (store.uiStore.getprojectEnvironmentLoader() &&
                'cursor-not-allowed pointer-events-none')
            }`}
          >
            <a
              id={item.tourId}
              onClick={(e) => {
                e.preventDefault()
                if (!store.uiStore.getGlobalLoader()) {
                  store.uiStore.setSidebarItem(item)
                  handleSidebarItemClick(item)
                  sessionStorage.setItem(
                    StringConstants.ACTIVE_TAB,
                    item.id.toString()
                  )
                }
              }}
              className={`relative flex items-center cursor-pointer justify-start py-[8px] px-[20px] w-full bg-transparent hover:bg-opacity-30 ${
                isSidebarExpanded ? '!px-[20px]  ' : 'justify-center h-[48px]'
              } ${
                store.uiStore.getGlobalLoader() ||
                store.uiStore.getprojectEnvironmentLoader() ||
                store.projectStore.getLoading()
                  ? 'cursor-not-allowed pointer-events-none'
                  : ''
              }`}
              style={{
                color:
                  sessionStorage.getItem(StringConstants.ACTIVE_TAB) ===
                  item.id.toString()
                    ? palette.colors.textDark1
                    : palette.colors.textDark6,
                backgroundColor:
                  sessionStorage.getItem(StringConstants.ACTIVE_TAB) ===
                  item.id.toString()
                    ? palette.colors.primary1
                    : 'transparent',
                position: 'relative',
                textDecoration: 'none'
              }}
            >
              {sessionStorage.getItem(StringConstants.ACTIVE_TAB) ===
                item.id.toString() && (
                <div
                  className={`absolute top-[0px] left-[0px] w-[8px] h-full`}
                  style={{ backgroundColor: palette.colors.primary2 }}
                />
              )}

              <div
                className={`flex items-center`}
                style={{
                  color:
                    sessionStorage.getItem(StringConstants.ACTIVE_TAB) ===
                    item.id.toString()
                      ? palette.colors.textDark1
                      : palette.colors.textDark6
                }}
              >
                {isSidebarExpanded ? (
                  <div className='w-[20px] h-[20px]'>{item.leftIcon}</div>
                ) : (
                  <>
                    <Tooltip
                      id={String(item.id)}
                      label={
                        <div style={{ color: pallete.colors.fillButtonhover }}>
                          {item.title}
                        </div>
                      }
                      className={`border override-tooltip-arrow max-w-[90%] break-all`}
                      position='right'
                      style={toolTipStyle}
                    >
                      <div className='w-[20px] h-[20px]'>{item.leftIcon}</div>
                    </Tooltip>
                  </>
                )}
                {isSidebarExpanded && (
                  <div
                    className={`pl-[12px] w-full flex flex-col max-w-[160px] flex-wrap ${
                      store.uiStore.getGlobalLoader() ||
                      store.projectStore.getLoading() ||
                      (store.uiStore.getprojectEnvironmentLoader() &&
                        'cursor-not-allowed pointer-events-none')
                    } `}
                  >
                    <DDSTypography.Paragraph
                      size='para'
                      variant={
                        sessionStorage.getItem(StringConstants.ACTIVE_TAB) ===
                        item.id.toString()
                          ? 'bold'
                          : 'medium'
                      }
                      color={
                        sessionStorage.getItem(StringConstants.ACTIVE_TAB) ===
                        item.id.toString()
                          ? palette.colors.textDark1
                          : palette.colors.textDark6
                      }
                    >
                      {item.sidebarTitle}
                    </DDSTypography.Paragraph>
                  </div>
                )}
              </div>
            </a>
          </div>
        </>
      ))}
    </div>
  )
})
SidebarLayout.displayName = 'SidebarLayout'
export default observer(SidebarLayout)
